import React, { useState, useEffect } from 'react';

import {
  FaSearch,
  FaPaperPlane,
  FaBullhorn,
  FaEnvelope,
  FaSms,
  FaBell,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaEye
} from 'react-icons/fa';
import { FiFilter, FiX, FiRefreshCw, FiSend, FiUsers } from 'react-icons/fi';
import validator from 'validator';
import axios from 'axios';
import axiosInstance from '../../../libs/axios';
import { useAuth } from '../../../provider/auth.provider';
import { getAssociationDisplayLabel } from '../../../utils/association.utils';

import toast from 'react-hot-toast';

interface SelectOption {
  value: string | number;
  label: string;
}

interface NotificationHistory {
  id: number;
  user_id: number;
  template_id?: number;
  type: 'email' | 'sms' | 'push';
  subject?: string;
  message: string;
  status: 'sent' | 'failed' | 'pending';
  read: boolean;
  sent_at: string;
  created_at: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
}

interface Role {
  id: number;
  name: string;
}

interface Template {
  id: number;
  name: string;
  type: string;
}

const SearchableSelect: React.FC<{
  options: SelectOption[];
  value: SelectOption | null;
  onChange: (option: SelectOption | null) => void;
  placeholder: string;
  error?: string;
  multiple?: boolean;
  selectedValues?: SelectOption[];
}> = ({ options, value, onChange, placeholder, error, multiple, selectedValues }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (option: SelectOption) => {
    if (multiple && selectedValues) {
      const exists = selectedValues.find(v => v.value === option.value);
      if (exists) {
        onChange(null);
      } else {
        onChange(option);
      }
    } else {
      onChange(option);
      setIsOpen(false);
    }
    setSearch('');
  };

  return (
    <div className="relative">
      <div
        className={`w-full px-3 py-2 border rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'
          }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {multiple && selectedValues && selectedValues.length > 0
          ? `${selectedValues.length} selected`
          : value
            ? value.label
            : placeholder}
      </div>
      {isOpen && (
        <div className="z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-auto">
          <div className="p-2">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

const NotificationManagement: React.FC = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<'send' | 'broadcast' | 'history'>('send');

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [history, setHistory] = useState<NotificationHistory[]>([]);
  const [historyFilters, setHistoryFilters] = useState({ user_id: '', limit: '50' });

  const [sendForm, setSendForm] = useState({
    templateName: '',
    userId: null as SelectOption | null,
    userIds: [] as SelectOption[],
    variables: {} as Record<string, string>,
    type: 'email' as 'email' | 'sms' | 'push',
    customMessage: { subject: '', message: '' },
    event: '',
    useTemplate: true
  });

  const [broadcastForm, setBroadcastForm] = useState({
    templateName: '',
    type: 'email' as 'email' | 'sms' | 'push',
    filters: {
      allRoles: false,
      roleIds: [] as SelectOption[],
      status: [] as SelectOption[],
      associationType: [] as SelectOption[]
    },
    variables: {} as Record<string, string>
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [variableFields, setVariableFields] = useState<string[]>([]);
  const [recipientMode, setRecipientMode] = useState<'single' | 'multiple'>('single');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  const fetchUsers = async (search = '') => {
    try {
      const response = await axiosInstance.get('/users/list', {
        params: { search, limit: 100 }
      });
      if (response.data.success) {
        setUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles/name');
      if (response.data.success) {
        setRoles(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get('/notification/templates');
      if (response.data.success) {
        const data = response.data.data;

        if (data?.templates) {
          // New paginated format: { templates: [...], pagination: {...} }
          setTemplates(data.templates || []);
        } else if (Array.isArray(data)) {
          // Old format: data is already an array
          setTemplates(data);
        } else {
          // Fallback to empty array to avoid runtime errors
          setTemplates([]);
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      if (!historyFilters.user_id) return;
      const response = await axiosInstance.get(
        `/notification/history/${historyFilters.user_id}`,
        { params: { limit: historyFilters.limit } }
      );
      if (response.data.success) {
        setHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateSendForm = () => {
    const newErrors: Record<string, string> = {};

    if (sendForm.useTemplate) {
      if (!sendForm.templateName || validator.isEmpty(sendForm.templateName)) {
        newErrors.templateName = 'Template is required';
      }
    } else {
      if (!sendForm.customMessage.message || validator.isEmpty(sendForm.customMessage.message.trim())) {
        newErrors.customMessage = 'Message is required';
      }
      if (sendForm.type === 'email' && (!sendForm.customMessage.subject || validator.isEmpty(sendForm.customMessage.subject.trim()))) {
        newErrors.customSubject = 'Subject is required for email';
      }
    }

    if (!sendForm.event) {
      if (!sendForm.userId && sendForm.userIds.length === 0) {
        newErrors.userId = 'At least one user must be selected';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBroadcastForm = () => {
    const newErrors: Record<string, string> = {};

    if (!broadcastForm.templateName || validator.isEmpty(broadcastForm.templateName)) {
      newErrors.templateName = 'Template is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendNotification = async () => {
    if (!validateSendForm()) return;

    setLoading(true);
    try {
      const data: any = {
        type: sendForm.type
      };

      if (sendForm.useTemplate) {
        data.templateName = sendForm.templateName;
        data.variables = sendForm.variables;
      } else {
        data.customMessage = sendForm.customMessage;
      }

      if (sendForm.event) {
        data.event = sendForm.event;
        data.userId = sendForm.userId?.value;
        data.variables = sendForm.variables;
      } else if (sendForm.userId) {
        data.userId = sendForm.userId.value;
      } else if (sendForm.userIds.length > 0) {
        data.userIds = sendForm.userIds.map(u => u.value);
      }

      await axiosInstance.post('/notification/send', data);

      setSendForm({
        templateName: '',
        userId: null,
        userIds: [],
        variables: {},
        type: 'email',
        customMessage: { subject: '', message: '' },
        event: '',
        useTemplate: true
      });
      setVariableFields([]);
      toast.success('Notification sent successfully!');
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleBroadcast = async () => {
    if (!validateBroadcastForm()) return;

    setLoading(true);
    try {
      const data: any = {
        templateName: broadcastForm.templateName,
        type: broadcastForm.type,
        variables: broadcastForm.variables
      };

      const filters: any = {};
      if (broadcastForm.filters.allRoles) {
        filters.allRoles = true;
      } else if (broadcastForm.filters.roleIds.length > 0) {
        filters.roleIds = broadcastForm.filters.roleIds.map(r => r.value);
      }
      if (broadcastForm.filters.status.length > 0) {
        filters.status = broadcastForm.filters.status.map(s => s.value);
      }
      if (broadcastForm.filters.associationType.length > 0) {
        filters.associationType = broadcastForm.filters.associationType.map(a => a.value);
      }

      if (Object.keys(filters).length > 0) {
        data.filters = filters;
      }

      await axiosInstance.post('/notification/broadcast', data);

      setBroadcastForm({
        templateName: '',
        type: 'email',
        filters: {
          allRoles: false,
          roleIds: [],
          status: [],
          associationType: []
        },
        variables: {}
      });
      setVariableFields([]);
      toast.success('Broadcast sent successfully!');
    } catch (error) {
      console.error('Error broadcasting:', error);
      toast.error('Failed to broadcast notification');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = async (templateName: string) => {
    const template = templates.find(t => t.name === templateName);
    if (template) {
      try {
        const response = await axiosInstance.get(`/notification/templates?search=${templateName}`);
        if (response.data.success && response.data.data.length > 0) {
          const fullTemplate = response.data.data[0];
          setVariableFields(fullTemplate.variables || []);
        }
      } catch (error) {
        console.error('Error fetching template details:', error);
      }
    }
  };

  const handleMarkAsRead = async (historyId: number) => {
    try {
      await axiosInstance.patch(`/notification/history/${historyId}/read`);
      fetchHistory();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaCheckCircle className="w-3 h-3" />
            Sent
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FaClock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <FaTimesCircle className="w-3 h-3" />
            Failed
          </span>
        );
      default:
        return null;
    }
  };

  const userOptions: SelectOption[] = users.map(u => ({
    value: u.id,
    label: `${u.first_name} ${u.last_name} (${u.email})`
  }));

  const roleOptions: SelectOption[] = roles.map(r => ({
    value: r.id,
    label: r.name
  }));

  const statusOptions: SelectOption[] = [
    { value: 'verified', label: 'Verified' },
    { value: 'initiated', label: 'Pending' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const associationTypeOptions: SelectOption[] = [
    { value: 'vendor', label: getAssociationDisplayLabel('vendor') },
    { value: 'inhouse', label: getAssociationDisplayLabel('inhouse') }
  ];

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <h2 className="text-xl font-semibold text-gray-900">Notification Management</h2>
      </div>

      <div className="border-b border-gray-200">
        <div className="flex">
          {hasPermission('notification', 'send') && (
            <button
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 ${activeTab === 'send'
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              onClick={() => setActiveTab('send')}
            >
              <FaPaperPlane className="w-4 h-4" />
              Send Notification
            </button>
          )}
          {hasPermission('notification', 'broadcast') && (
            <button
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 ${activeTab === 'broadcast'
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              onClick={() => setActiveTab('broadcast')}
            >
              <FaBullhorn className="w-4 h-4" />
              Broadcast
            </button>
          )}
          {hasPermission('notification', 'view_history') && (
            <button
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 ${activeTab === 'history'
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              onClick={() => setActiveTab('history')}
            >
              <FaClock className="w-4 h-4" />
              History
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'send' && hasPermission('notification', 'send') && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notification Type *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={sendForm.type}
                  onChange={(e) => setSendForm({ ...sendForm, type: e.target.value as any })}
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="push">Push Notification</option>
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={sendForm.useTemplate}
                    onChange={(e) => setSendForm({ ...sendForm, useTemplate: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">Use Template</span>
                </label>
              </div>
            </div>

            {sendForm.useTemplate ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template *</label>
                  {/*
                  <select
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.templateName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={sendForm.templateName}
                    onChange={(e) => {
                      setSendForm({ ...sendForm, templateName: e.target.value });
                      handleTemplateChange(e.target.value);
                    }}
                  >
                    <option value="">Select Template</option>
                    {templates
                      .filter(t => t.type === sendForm.type)
                      .map((template) => (
                        <option key={template.id} value={template.name}>
                          {template.name}
                        </option>
                      ))}
                  </select>
                  */}
                  <SearchableSelect
                    options={templates
                      .filter(t => t.type === sendForm.type)
                      .map((template) => ({ value: template.name, label: template.name }))}
                    value={sendForm.templateName
                      ? { value: sendForm.templateName, label: sendForm.templateName }
                      : null}
                    onChange={(option) => {
                      const templateName = (option?.value as string) || '';
                      setSendForm({ ...sendForm, templateName });
                      if (templateName) {
                        handleTemplateChange(templateName);
                      }
                    }}
                    placeholder="Select Template"
                    error={errors.templateName}
                  />
                  {errors.templateName && <p className="text-red-500 text-xs mt-1">{errors.templateName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Send By Event</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., register, login"
                    value={sendForm.event}
                    onChange={(e) => setSendForm({ ...sendForm, event: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to send to specific users</p>
                </div>
              </>
            ) : (
              <>
                {sendForm.type === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.customSubject ? 'border-red-500' : 'border-gray-300'
                        }`}
                      value={sendForm.customMessage.subject}
                      onChange={(e) =>
                        setSendForm({
                          ...sendForm,
                          customMessage: { ...sendForm.customMessage, subject: e.target.value }
                        })
                      }
                    />
                    {errors.customSubject && <p className="text-red-500 text-xs mt-1">{errors.customSubject}</p>}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.customMessage ? 'border-red-500' : 'border-gray-300'
                      }`}
                    rows={6}
                    value={sendForm.customMessage.message}
                    onChange={(e) =>
                      setSendForm({
                        ...sendForm,
                        customMessage: { ...sendForm.customMessage, message: e.target.value }
                      })
                    }
                  />
                  {errors.customMessage && <p className="text-red-500 text-xs mt-1">{errors.customMessage}</p>}
                </div>
              </>
            )}

            {!sendForm.event && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Send To</label>
                  <div className="flex rounded border border-gray-300 overflow-hidden w-fit">
                    <button
                      type="button"
                      className={`px-4 py-2 text-sm font-medium transition-colors ${recipientMode === 'single'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      onClick={() => {
                        setRecipientMode('single');
                        setSendForm({ ...sendForm, userIds: [] });
                      }}
                    >
                      <FiUsers className="w-4 h-4 inline mr-1.5" />
                      Single User
                    </button>
                    <button
                      type="button"
                      className={`px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${recipientMode === 'multiple'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      onClick={() => {
                        setRecipientMode('multiple');
                        setSendForm({ ...sendForm, userId: null });
                      }}
                    >
                      <FiUsers className="w-4 h-4 inline mr-1.5" />
                      Multiple Users
                    </button>
                  </div>
                </div>

                {recipientMode === 'single' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select User *</label>
                    <SearchableSelect
                      options={userOptions}
                      value={sendForm.userId}
                      onChange={(option) => setSendForm({ ...sendForm, userId: option, userIds: [] })}
                      placeholder="Search and select a user"
                      error={errors.userId}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Users *</label>
                    <SearchableSelect
                      options={userOptions}
                      value={null}
                      onChange={(option) => {
                        if (option && !sendForm.userIds.find(u => u.value === option.value)) {
                          setSendForm({ ...sendForm, userIds: [...sendForm.userIds, option], userId: null });
                        }
                      }}
                      placeholder="Search and select users"
                      multiple
                      selectedValues={sendForm.userIds}
                    />
                    {sendForm.userIds.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sendForm.userIds.map((user) => (
                          <span
                            key={user.value}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            {user.label}
                            <button
                              onClick={() =>
                                setSendForm({
                                  ...sendForm,
                                  userIds: sendForm.userIds.filter(u => u.value !== user.value)
                                })
                              }
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <FiX className="w-4 h-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {variableFields.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Template Variables</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {variableFields.map((variable) => (
                    <div key={variable}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{variable}</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={sendForm.variables[variable] || ''}
                        onChange={(e) =>
                          setSendForm({
                            ...sendForm,
                            variables: { ...sendForm.variables, [variable]: e.target.value }
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasPermission('notification', 'send') && (
              <button
                onClick={handleSendNotification}
                disabled={loading}
                className="px-6 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 flex items-center gap-2 disabled:opacity-50"
              >
                <FiSend className="w-4 h-4" />
                {loading ? 'Sending...' : 'Send Notification'}
              </button>
            )}
          </div>
        )}

        {activeTab === 'broadcast' && hasPermission('notification', 'broadcast') && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notification Type *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={broadcastForm.type}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, type: e.target.value as any })}
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="push">Push Notification</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template *</label>
                <select
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.templateName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  value={broadcastForm.templateName}
                  onChange={(e) => {
                    setBroadcastForm({ ...broadcastForm, templateName: e.target.value });
                    handleTemplateChange(e.target.value);
                  }}
                >
                  <option value="">Select Template</option>
                  {templates
                    .filter(t => t.type === broadcastForm.type)
                    .map((template) => (
                      <option key={template.id} value={template.name}>
                        {template.name}
                      </option>
                    ))}
                </select>
                {errors.templateName && <p className="text-red-500 text-xs mt-1">{errors.templateName}</p>}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Filter Recipients</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                  <label className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={broadcastForm.filters.allRoles}
                      onChange={(e) =>
                        setBroadcastForm({
                          ...broadcastForm,
                          filters: {
                            ...broadcastForm.filters,
                            allRoles: e.target.checked,
                            roleIds: e.target.checked ? [] : broadcastForm.filters.roleIds
                          }
                        })
                      }
                      className="h-4 w-4"
                    />
                    Send to all roles
                  </label>
                  <SearchableSelect
                    options={roleOptions}
                    value={null}
                    onChange={(option) => {
                      if (option && !broadcastForm.filters.roleIds.find(r => r.value === option.value)) {
                        setBroadcastForm({
                          ...broadcastForm,
                          filters: {
                            ...broadcastForm.filters,
                            allRoles: false,
                            roleIds: [...broadcastForm.filters.roleIds, option]
                          }
                        });
                      }
                    }}
                    placeholder="Select roles"
                    multiple
                    selectedValues={broadcastForm.filters.roleIds}
                  />
                  {broadcastForm.filters.allRoles && (
                    <p className="mt-2 text-xs text-blue-600">Every role will receive this broadcast.</p>
                  )}
                  {broadcastForm.filters.roleIds.length > 0 && !broadcastForm.filters.allRoles && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {broadcastForm.filters.roleIds.map((role) => (
                        <span
                          key={role.value}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {role.label}
                          <button
                            onClick={() =>
                              setBroadcastForm({
                                ...broadcastForm,
                                filters: {
                                  ...broadcastForm.filters,
                                  roleIds: broadcastForm.filters.roleIds.filter(r => r.value !== role.value)
                                }
                              })
                            }
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <SearchableSelect
                    options={statusOptions}
                    value={null}
                    onChange={(option) => {
                      if (option && !broadcastForm.filters.status.find(s => s.value === option.value)) {
                        setBroadcastForm({
                          ...broadcastForm,
                          filters: {
                            ...broadcastForm.filters,
                            status: [...broadcastForm.filters.status, option]
                          }
                        });
                      }
                    }}
                    placeholder="Select status"
                    multiple
                    selectedValues={broadcastForm.filters.status}
                  />
                  {broadcastForm.filters.status.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {broadcastForm.filters.status.map((status) => (
                        <span
                          key={status.value}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {status.label}
                          <button
                            onClick={() =>
                              setBroadcastForm({
                                ...broadcastForm,
                                filters: {
                                  ...broadcastForm.filters,
                                  status: broadcastForm.filters.status.filter(s => s.value !== status.value)
                                }
                              })
                            }
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Association Type</label>
                  <SearchableSelect
                    options={associationTypeOptions}
                    value={null}
                    onChange={(option) => {
                      if (option && !broadcastForm.filters.associationType.find(a => a.value === option.value)) {
                        setBroadcastForm({
                          ...broadcastForm,
                          filters: {
                            ...broadcastForm.filters,
                            associationType: [...broadcastForm.filters.associationType, option]
                          }
                        });
                      }
                    }}
                    placeholder="Select type"
                    multiple
                    selectedValues={broadcastForm.filters.associationType}
                  />
                  {broadcastForm.filters.associationType.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {broadcastForm.filters.associationType.map((type) => (
                        <span
                          key={type.value}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {type.label}
                          <button
                            onClick={() =>
                              setBroadcastForm({
                                ...broadcastForm,
                                filters: {
                                  ...broadcastForm.filters,
                                  associationType: broadcastForm.filters.associationType.filter(
                                    a => a.value !== type.value
                                  )
                                }
                              })
                            }
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {variableFields.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Template Variables</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {variableFields.map((variable) => (
                    <div key={variable}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{variable}</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={broadcastForm.variables[variable] || ''}
                        onChange={(e) =>
                          setBroadcastForm({
                            ...broadcastForm,
                            variables: { ...broadcastForm.variables, [variable]: e.target.value }
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasPermission('notification', 'broadcast') && (
              <button
                onClick={handleBroadcast}
                disabled={loading}
                className="px-6 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 flex items-center gap-2 disabled:opacity-50"
              >
                <FaBullhorn className="w-4 h-4" />
                {loading ? 'Broadcasting...' : 'Broadcast Notification'}
              </button>
            )}
          </div>
        )}

        {activeTab === 'history' && hasPermission('notification', 'view_history') && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select User *</label>
                <SearchableSelect
                  options={userOptions}
                  value={
                    historyFilters.user_id
                      ? userOptions.find(u => u.value.toString() === historyFilters.user_id) || null
                      : null
                  }
                  onChange={(option) =>
                    setHistoryFilters({ ...historyFilters, user_id: option ? option.value.toString() : '' })
                  }
                  placeholder="Select a user to view history"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Limit</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={historyFilters.limit}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, limit: e.target.value })}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>

            {hasPermission('notification', 'view_history') && (
              <button
                onClick={fetchHistory}
                disabled={!historyFilters.user_id || loading}
                className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 flex items-center gap-2 disabled:opacity-50"
              >
                <FiRefreshCw className="w-4 h-4" />
                Load History
              </button>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Read</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Sent At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : history.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        {historyFilters.user_id
                          ? 'No notification history found for this user'
                          : 'Select a user to view their notification history'}
                      </td>
                    </tr>
                  ) : (
                    history.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {item.type === 'email' && <FaEnvelope className="w-4 h-4 text-blue-600" />}
                            {item.type === 'sms' && <FaSms className="w-4 h-4 text-green-600" />}
                            {item.type === 'push' && <FaBell className="w-4 h-4 text-purple-600" />}
                            <span className="text-sm capitalize">{item.type}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-900">{item.subject || '-'}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="max-w-md whitespace-pre-wrap break-words text-sm text-gray-600" title={item.message}>
                            {item.message}
                          </div>
                        </td>
                        <td className="px-4 py-4">{getStatusBadge(item.status)}</td>
                        <td className="px-4 py-4">
                          {item.read ? (
                            <span className="text-green-600 text-sm">Read</span>
                          ) : (
                            <span className="text-gray-400 text-sm">Unread</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-600">
                            {new Date(item.sent_at).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          {!item.read && hasPermission('notification', 'mark_read') && (
                            <button
                              onClick={() => handleMarkAsRead(item.id)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Mark as Read
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationManagement;
